/* iter237 — Billing Inbox tab. Every unresolved office-action invoice, oldest
   received first, independent of any service-date filter. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Inbox, RefreshCw, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (d) => { try { return d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'; } catch { return '—'; } };
const REASON_LABEL = {
  office_to_bill: 'Office to Bill', awaiting_office_release: 'Awaiting Office Release',
  warranty_draft: 'Warranty Draft', needs_review: 'Needs Review', ready_for_qb: 'Ready for QB',
  qb_aging: 'QB Aging', missing_po: 'Missing PO', po_conflict: 'PO Conflict', no_amount: 'No Amount',
  paid_no_match: 'Payment Mismatch', overcollected: 'Overcollected', not_in_qb: 'Not in QB',
  balance_mismatch: 'Balance Mismatch', duplicate: 'Possible Duplicate', warranty_unsubmitted: 'Warranty Unsubmitted',
};
const PO_COLOR = { ok: 'text-green-400', none: 'text-[#525252]', missing: 'text-red-400', conflict: 'text-amber-400' };

const BillingInboxTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/billing/inbox`, { withCredentials: true });
      setRows(data.invoices || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load inbox'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="billing-inbox" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-['Outfit'] text-xl font-semibold text-white flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#D4AF37]" /> Billing Inbox
            <span className="text-[#A3A3A3] text-sm font-normal">· {rows.length} unresolved</span>
          </h3>
          <p className="text-[#737373] text-xs mt-1 max-w-3xl">Every invoice awaiting office action — oldest received first. Stays here until its office action is resolved. Not affected by service-date filters.</p>
        </div>
        <button data-testid="billing-inbox-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-xs text-white bg-[#262626] hover:bg-[#333] rounded px-3 py-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full min-w-[1050px] text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3] text-xs">
            <tr>
              {['Received', 'Service', 'Customer', 'Address', 'Tech', 'Total', 'Paid', 'Balance', 'Outcome', 'WO#', 'PO', 'QB', 'Age', ''].map((h) => (
                <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={14} className="px-3 py-8 text-center text-[#525252]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={14} className="px-3 py-8 text-center text-green-400">Inbox zero — no unresolved office-action invoices. 🎉</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`inbox-row-${r.id}`} className="border-b border-[#262626] last:border-0 hover:bg-[#1a1a1a]">
                <td className="px-3 py-2 whitespace-nowrap text-white text-xs">{dt(r.office_received_at)}{r.office_received_is_fallback && <span className="text-[#525252]" title="Estimated"> ~</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[#A3A3A3] text-xs">{r.service_date || '—'}</td>
                <td className="px-3 py-2 text-white">{r.customer_name || '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3] max-w-[160px] truncate" title={r.address}>{r.address || '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap">{r.technician_name}</td>
                <td className="px-3 py-2 text-white whitespace-nowrap">{money(r.invoice_total)}</td>
                <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap">{money(r.amount_paid)}</td>
                <td className={`px-3 py-2 whitespace-nowrap font-semibold ${Number(r.balance_due) > 0 ? 'text-amber-400' : 'text-green-400'}`}>{money(r.balance_due)}</td>
                <td className="px-3 py-2 text-[#A3A3A3] text-xs whitespace-nowrap">{r.billing_outcome}</td>
                <td className="px-3 py-2 text-[#A3A3A3] text-xs">{r.work_order_number || '—'}</td>
                <td className={`px-3 py-2 text-xs capitalize ${PO_COLOR[r.po_status] || 'text-[#A3A3A3]'}`}>{r.po_status}</td>
                <td className="px-3 py-2 text-[#A3A3A3] text-xs whitespace-nowrap">{(r.qb_status || '').replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap"><span className={Number(r.age_in_queue_days) >= 3 ? 'text-red-400 font-bold' : 'text-[#A3A3A3]'}>{r.age_in_queue_days ?? 0}d</span></td>
                <td className="px-3 py-2">
                  <Link to={`/admin/invoices?status=all&open=${r.id}`} className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-bold whitespace-nowrap"><ExternalLink className="w-3 h-3" /> Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(rows.reduce((acc, r) => { (r.reasons || []).forEach((x) => { acc[x] = (acc[x] || 0) + 1; }); return acc; }, {})).map(([k, v]) => (
            <span key={k} className="bg-[#0A0A0A] border border-[#262626] rounded-full px-2.5 py-1 text-[11px] text-[#A3A3A3]">{REASON_LABEL[k] || k}: <span className="text-white font-bold">{v}</span></span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingInboxTab;
